import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { wotdApi, type WordOfTheDay } from '@/api/wotd';
import { vocabApi } from '@/api/vocab';
import { rememberAudio } from '@/lib/wordAudio';
import { AudioPlayButton } from '@/components/ui/AudioPlayButton';
import { useWordAudio } from '@/hooks/useWordAudio';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';

function shuffle<T>(arr: T[]): T[] {
  const r = [...arr];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [r[i], r[j]] = [r[j], r[i]];
  }
  return r;
}

/**
 * Word of the Day — the shared, AI-generated daily word (same for everyone,
 * refreshes at local midnight). The card is shown ONLY while today's word
 * exists and this student hasn't studied it yet; once studied it disappears
 * until tomorrow. "Изучить" opens a study modal with a "build the sentence"
 * mini-game that must be solved to mark the word as learned.
 */
export function WordOfTheDayCard({ tone = 'violet' }: { tone?: 'violet' | 'info' }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const wotd = useQuery({ queryKey: ['student', 'wotd'], queryFn: wotdApi.getToday });
  // Тот же ключ, что на главной, — второго запроса не будет.
  const vocab = useQuery({ queryKey: ['vocab', 'stats'], queryFn: vocabApi.getStats, enabled: tone === 'info' });
  const study = useMutation({
    mutationFn: () => wotdApi.study(),
    onSuccess: () => {
      setOpen(false);
      qc.invalidateQueries({ queryKey: ['student', 'wotd'] });
    },
  });

  const w = wotd.data?.word;
  // Hidden when there's no word, it's already studied, or the request failed.
  if (!w || wotd.data?.studied) return null;

  // Голубая карточка из макета «Desktop Web v2» — для кабинета на компьютере.
  // Телефон остаётся на прежней сиреневой: мобильную часть в этот заход не
  // трогаем, поэтому вид выбирается снаружи, а не заменяется здесь.
  if (tone === 'info')
    return (
      <>
        <div className="rounded-[20px] bg-info-container px-[18px] py-4">
          <p className="text-[9.5px] font-bold uppercase tracking-[0.12em] text-info">{t('wotd.kicker')}</p>
          <h3 className="mb-[3px] mt-[9px] font-display text-[25px] font-bold leading-[1.05] text-on-surface">{w.word}</h3>
          <p className="text-[12px] text-on-surface-variant">
            {[w.ipa, w.part_of_speech, i18n.language === 'kk' ? w.translation_kk : w.translation_ru]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <div className="mt-[13px] flex items-center gap-2">
            <button
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-info px-4 py-[9px] text-[12.5px] font-bold text-white transition hover:brightness-110"
            >
              {t('wotd.study')}
              <span className="material-symbols-outlined text-[14px]">school</span>
            </button>
            <span className="text-[11px] text-on-surface-variant">
              {t('dashboard.vocabStat', { learned: vocab.data?.learned ?? 0, learning: Math.max((vocab.data?.total ?? 0) - (vocab.data?.learned ?? 0), 0) })}
            </span>
          </div>
        </div>

        {open && <StudyModal word={w} saving={study.isPending} onClose={() => setOpen(false)} onDone={() => study.mutate()} />}
      </>
    );

  return (
    <>
      <Card className="relative overflow-hidden !bg-[#EEE9FF]">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#6D5AE0]">{t('wotd.kicker')}</p>
        <div className="mt-1 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display text-3xl font-extrabold text-[#3A2E8C]">{w.word}</h3>
            {w.ipa && <p className="mt-0.5 text-sm text-[#6D5AE0]">{w.ipa}</p>}
            <button
              onClick={() => setOpen(true)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-button bg-[#6D5AE0] px-4 py-2 text-sm font-semibold text-white shadow-soft transition hover:brightness-105"
            >
              {t('wotd.study')}
              <span className="material-symbols-outlined text-base">school</span>
            </button>
          </div>
          <img
            src="/wotd-book.webp"
            alt=""
            className="-mt-1 h-24 w-24 flex-none object-contain drop-shadow-md"
          />
        </div>
      </Card>

      {open && <StudyModal word={w} saving={study.isPending} onClose={() => setOpen(false)} onDone={() => study.mutate()} />}
    </>
  );
}

function StudyModal({
  word,
  saving,
  onClose,
  onDone,
}: {
  word: WordOfTheDay;
  saving: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const target = (word.example ?? '').trim();
  const tokens = useMemo(() => target.split(/\s+/).filter(Boolean), [target]);
  const hasGame = tokens.length > 1;
  const [pool, setPool] = useState<string[]>(() => shuffle(tokens));
  const [picked, setPicked] = useState<string[]>([]);
  const solved = !hasGame || picked.join(' ') === tokens.join(' ');
  const full = picked.length === tokens.length;

  // Слово дня звучит тем же голосом, что словарь и урок.
  //
  // ⚠️ КНОПКА ЖДЁТ, ПОКА ЗАПИСЬ СКАЧАЕТСЯ (05.09.2026, требование заказчика:
  // «сделать как в camp — на кнопке значок загрузки, и только потом нажатие»).
  //
  // Раньше нажатие запускало запрос к серверу, и пока он шёл, разрешение на
  // звук, выданное браузером под нажатие пальцем, истекало — проигрывание
  // отказывало МОЛЧА. Снаружи это ровно «кнопка не работает».
  const audio = useWordAudio(word.word, word.audio_url);

  useEffect(() => {
    rememberAudio({ [word.word]: word.audio_url });
  }, [word.word, word.audio_url]);

  return (
    <Modal open onClose={onClose} title={t('wotd.modalTitle')} size="md">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <h3 className="font-display text-3xl font-extrabold text-[#3A2E8C]">{word.word}</h3>
          {/*
            ⚠️ КНОПКА ВИДНА ВСЕГДА (требование заказчика 05.09.2026: «показывай
            кнопку слушания, но пусть она загружается и виден прогресс»).
            Прятать её нельзя: человек стоит и ждёт кнопку, которой нет.

            Пока запись качается — кружок ожидания и нажатие выключено.
            Не удалось получить — кнопка гаснет, но остаётся: видно, что звук
            есть в замысле, просто сейчас недоступен.
          */}
          {/*
            ⚠️ КНОПКА ВИДНА ВСЕГДА (требование заказчика 05.09.2026: «показывай
            кнопку слушания, но пусть она загружается и виден прогресс»).
            Прятать её нельзя: человек стоит и ждёт кнопку, которой нет.

            Вид — общий с остальным приложением (AudioPlayButton), а СОСТОЯНИЕ
            своё: здесь запись скачивается целиком до нажатия («как в camp»),
            и эту стратегию менять нельзя. Отсюда state/onPlay снаружи.
          */}
          <AudioPlayButton
            state={audio.failed ? 'error' : !audio.ready ? 'loading' : audio.playing ? 'playing' : 'idle'}
            onPlay={audio.play}
            className="flex-none bg-[#EEE9FF] text-[#6D5AE0] rounded-full h-10 w-10"
          />
          {/*
            Процент виден, только пока запись едет и число честное: запрос наш,
            длина известна. Заказчик просил прогресс ещё 05.09.2026, а кнопка
            до сих пор показывала одну крутилку.
          */}
          {!audio.ready && !audio.failed && audio.percent != null && (
            <span className="flex-none text-xs tabular-nums text-on-surface-variant">
              {t('audioPlayer.loadedPercent', { percent: audio.percent })}
            </span>
          )}
          {word.part_of_speech && <span className="text-xs text-on-surface-variant">{word.part_of_speech}</span>}
          {word.level && (
            <span className="rounded-pill bg-primary-fixed px-2 py-0.5 text-xs font-semibold text-on-primary-fixed-variant">
              {word.level}
            </span>
          )}
        </div>
        {word.ipa && <p className="-mt-2 text-sm text-[#6D5AE0]">{word.ipa}</p>}
        <p className="font-semibold text-on-surface">
          {word.translation_ru}
          {word.translation_kk ? ` · ${word.translation_kk}` : ''}
        </p>
        {word.definition && <p className="text-sm text-on-surface-variant">{word.definition}</p>}

        {hasGame && (
          <div className="rounded-card bg-surface-container-low p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('wotd.buildSentence')}
            </p>
            <div className="mb-2 flex min-h-[2.75rem] flex-wrap items-center gap-2 rounded-button border border-dashed border-outline-variant p-2">
              {picked.length === 0 && (
                <span className="text-sm text-on-surface-variant">{t('wotd.tapInOrder')}</span>
              )}
              {picked.map((wd, i) => (
                <button
                  key={`${wd}-${i}`}
                  className="ex-word"
                  onClick={() => {
                    setPicked((p) => p.filter((_, j) => j !== i));
                    setPool((p) => [...p, wd]);
                  }}
                >
                  {wd}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {pool.map((wd, i) => (
                <button
                  key={`${wd}-${i}`}
                  className="ex-word"
                  onClick={() => {
                    setPicked((p) => [...p, wd]);
                    setPool((p) => p.filter((_, j) => j !== i));
                  }}
                >
                  {wd}
                </button>
              ))}
            </div>
            {full && !solved && (
              <p className="mt-2 text-sm text-error">{t('wotd.almost')}</p>
            )}
            {solved && <p className="mt-2 text-sm font-medium text-success">{t('wotd.correct')}</p>}
          </div>
        )}

        {/* Reveal the full example only once the sentence is assembled. */}
        {solved && word.example && (
          <p className="text-sm italic text-on-surface-variant">«{word.example}»</p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('wotd.close')}</Button>
          <Button disabled={!solved} loading={saving} onClick={onDone}>
            <span className="material-symbols-outlined text-base">check</span>
            {t('wotd.done')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
